import { useAction } from "convex/react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../../convex/_generated/api";
import { errorCodes } from "../../../convex/errorCodes";
import { PreviewLoadingCard } from "../components/PreviewLoadingCard";
import { ProfilePreviewCard } from "../components/ProfilePreviewCard";
import { useProfileSearch } from "../hooks/useProfileSearch";
import {
  BILLING_ERROR_CODES,
  buildLoggedBillingPath,
} from "../lib/billingGuards";
import { WEB_ACTION_TIMEOUT_MS, withTimeout } from "../lib/withTimeout";
import { t } from "../i18n";

const previewLoadingSteps = [
  "searchLoading1",
  "searchLoading2",
  "searchLoading3",
  "searchLoading4",
  "searchLoading5",
];

type CreateMonitorResult = {
  success?: boolean;
  error?: string;
};

function getFriendlyMessage(code: string | undefined): string {
  if (!code) return errorCodes.TARGET_INFO_UNAVAILABLE.friendlyMessage;
  const entry = Object.values(errorCodes).find(
    (value) => typeof value !== "function" && value.code === code,
  );
  return entry && typeof entry !== "function"
    ? entry.friendlyMessage
    : errorCodes.TARGET_INFO_UNAVAILABLE.friendlyMessage;
}

export function AppAddTargetPreviewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const createMonitor = useAction(api.monitors.createMonitorAfterPurchase);
  const handle = searchParams.get("handle") ?? "";
  const { state, search, reset } = useProfileSearch(handle);
  const [isStarting, setIsStarting] = useState(false);
  const [monitorError, setMonitorError] = useState<string | null>(null);
  const isSearching = state.phase === "validating" || state.phase === "loading";

  useEffect(() => {
    if (handle) void search(handle);
    return () => reset();
  }, [handle, reset, search]);

  async function startMonitoring() {
    if (isStarting) return;

    setIsStarting(true);
    setMonitorError(null);

    try {
      const result = (await withTimeout(
        createMonitor({ handle, source: "home" }),
        WEB_ACTION_TIMEOUT_MS,
        "Web monitor action timed out",
      )) as CreateMonitorResult | undefined;

      if (result?.success) {
        navigate("/app", { replace: true });
        return;
      }

      const code = result?.error;
      if (code && BILLING_ERROR_CODES.has(code)) {
        navigate(buildLoggedBillingPath({ handle, reason: code }), {
          replace: true,
        });
        return;
      }

      setMonitorError(getFriendlyMessage(code));
    } catch (caught: unknown) {
      const code = (caught as { data?: { code?: string } })?.data?.code;
      if (code && BILLING_ERROR_CODES.has(code)) {
        navigate(buildLoggedBillingPath({ handle, reason: code }), {
          replace: true,
        });
        return;
      }
      setMonitorError(getFriendlyMessage(code));
    } finally {
      setIsStarting(false);
    }
  }

  return (
    <div className="app-page app-add-target-preview-page st-preview-page">
      <button
        type="button"
        onClick={() => navigate("/app/add-target")}
        className="app-back-button"
      >
        <span aria-hidden="true">←</span>
        <span>{t("commonBack")}</span>
      </button>

      <section
        className={`app-target-preview-stage${isSearching ? " is-loading" : ""}`}
        aria-live="polite"
      >
        {state.phase === "validating" && (
          <PreviewLoadingCard
            handle={handle}
            progress={0.08}
            steps={previewLoadingSteps}
          />
        )}

        {state.phase === "loading" && (
          <PreviewLoadingCard
            handle={handle}
            progress={state.progress}
            steps={state.steps}
            profilePicUrl={state.data?.profile?.profilePicUrl}
          />
        )}

        {state.phase === "success" && (
          <>
            {monitorError && (
              <p className="st-preview-action-error" role="alert">
                {monitorError}
              </p>
            )}
            <ProfilePreviewCard
              data={state.data}
              authenticated
              ctaLabel={t("previewAddMonitoring")}
              loadingLabel={t("loadingAddMonitorTitle")}
              isLoading={isStarting}
              onStartMonitoring={() => void startMonitoring()}
              onClose={() => navigate("/app/add-target")}
            />
          </>
        )}

        {state.phase === "error" && (
          <article className="st-preview-state-card st-preview-error-card">
            <h2>{t("commonError")}</h2>
            <p>{state.message}</p>
            <div className="st-preview-error-actions">
              <button
                type="button"
                onClick={() => void search(handle)}
                className="st-preview-ghost-button"
              >
                {t("commonRetry")}
              </button>
            </div>
          </article>
        )}
      </section>
    </div>
  );
}
